import { Component, EventEmitter, Input, Output, signal, OnInit } from '@angular/core';
import { PatientModel } from '../../../core/api/models/patient-model';
import { FormGroup, Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { PatientSessionModel } from '../../../core/api/models/patient-session-model';
import { PatientBehaviorModel } from '../../../core/api/models/patient-behavior-model';
import { PatientBehaviorAntecedentModel, PatientBehaviorConsequenceModel, PatientBehaviorDataModel, PatientBehaviorLocationModel, PatientBehaviorResponseModel, PatientBehaviorSeverityModel, PatientBehaviorType, PatientTargetDataModel, SessionSimpleDataModel, UserModel } from '../../../core/api/models';
import { SelectItem } from 'primeng/api';
import { AuthService } from '../../../auth/auth.service';
import { SupervisorApiService } from '../../../core/api/services/supervisor-api.service';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { GraphApiService } from '../../../core/api/services/graph-api.service';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import { forkJoin } from 'rxjs';
import { DataTakerStaffDataModel } from '../../../core/api/models';

@Component({
  selector: 'app-add-behavior-data',
  templateUrl: './add-behavior-data.component.html',
  styleUrl: './add-behavior-data.component.css',
  standalone: false,
})
export class AddBehaviorDataComponent implements OnInit {

  @Input({ required: true }) selectedPatientID!: number;
  @Input({ required: false }) behavior!: PatientBehaviorModel;
  @Input({ required: false }) patientSession!: PatientSessionModel;
  @Input({ required: false }) hideBehaviorSelector: boolean = false;
  @Input({ required: false }) hideSessionSelector: boolean = false;
  @Output() close = new EventEmitter<boolean>();

  isLoading = signal(false);

  addBehaviorForm = new FormGroup({
    recordedDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
    behavior: new FormControl<PatientBehaviorModel | null>(null, { validators: [Validators.required] }),
    selectedAntecedent: new FormControl<PatientBehaviorAntecedentModel | null>(null),
    antecedentOther: new FormControl<string | null>(null),
    selectedSeverity: new FormControl<PatientBehaviorSeverityModel | null>(null),
    selectedResponse: new FormControl<PatientBehaviorResponseModel | null>(null),
    selectedLocation: new FormControl<PatientBehaviorLocationModel | null>(null),
    selectedConsequence: new FormControl<PatientBehaviorConsequenceModel | null>(null),
    consequenceOther: new FormControl<string | null>(null),
    textValue: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    duration: new FormControl<number | null>(null),
    happenedDuringInterval: new FormControl<boolean>(false),
    selectedStaff: new FormControl< SelectItem<DataTakerStaffDataModel> | null>(null),
  });

  titleName: string = 'Add Behavior Data';
  actionName: string = 'Add';

  antecedents = signal<PatientBehaviorAntecedentModel[]>([]);
  consequences = signal<PatientBehaviorConsequenceModel[]>([]);
  severities = signal<PatientBehaviorSeverityModel[]>([]);
  responses = signal<PatientBehaviorResponseModel[]>([]);
  locations = signal<PatientBehaviorLocationModel[]>([]);

  sessions = signal<SelectItem<SessionSimpleDataModel>[]>([]);

  selectedBehavior = signal<PatientBehaviorModel | null>(null);
  selectedAntecedents = signal<PatientBehaviorAntecedentModel[] | null>([]);
  selectedConsequences = signal<PatientBehaviorConsequenceModel[] | null>([]);
  selectedSeverity = signal<PatientBehaviorSeverityModel | null>(null);
  selectedResponse = signal<PatientBehaviorResponseModel | null>(null);
  selectedLocation = signal<PatientBehaviorLocationModel | null>(null);
  selectedSession: SessionSimpleDataModel | null = null;

  behaviors = signal<SelectItem[]>([]);

  user: UserModel | null = null;
  
  staffMembers = signal<DataTakerStaffDataModel[]>([]);
  staffOptions = signal<SelectItem<DataTakerStaffDataModel>[]>([]);

  
  creatingSession = signal<boolean>(false);
  newSessionStartDate: string | null = null;
  newSessionEndDate: string | null = null;

  constructor(
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private graphApiService: GraphApiService,
    private alertService: AlertService,
    private authService: AuthService,
  ) {

  }

  ngOnInit(): void {
    this.authService.user().subscribe((user) => {
      this.user = user;
    });

    if (this.behavior) {
      this.selectedBehavior.set(this.behavior);
      this.addBehaviorForm.controls.behavior.setValue(this.behavior);
    }

    if (this.patientSession) {
      this.hideSessionSelector = true;
      this.selectedSession = this.patientSession;
      this.loadStaffFromSession(this.patientSession);
    }

    if (!this.hideSessionSelector) {
      this.loadSessions();
    }

    this.loadAllData();
  }

  loadSessions() {
    this.isLoading.set(true);
    this.staffApiService.GetPatientSessionSimpleList({ patientID: this.selectedPatientID ?? 0 }).subscribe({
      next: (sessions) => {
        this.sessions.set(sessions.map((s) => ({ value: s, label: this.formattedDates(s.StartedDate, s.FinishedDate) + (s.ServiceID ? (' (Service: #' + s.ServiceID + ' - ' + (s.Staffs?.map(st => st.FullName ?? '').join(', ') ?? []) + ')') : '') })));
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  
  showCreateSession() {
    this.creatingSession.set(true);
    this.selectedSession = null;
  }

  hideCreateSession() {
    this.creatingSession.set(false);
    this.newSessionStartDate = null;
    this.newSessionEndDate = null;
  }

  

  changedSession(event: any) {
    console.log(event);
    
    // Update selectedSession when session changes
    this.selectedSession = event.value;
    
    // Reload staff members if behavior is already selected
    // This ensures staff from the selected session are included
    if (this.selectedBehavior()) {
      this.loadStaffMembersForSelection();
    }
  }
  
  loadStaffMembersForSelection() {
    const selectedSessionValue = this.selectedSession;

    // if no session is selected, just return;
    if (!selectedSessionValue) {
      return;
    } 
    
    if (!selectedSessionValue.ServiceID) {
      // if session has no service, just use the current user
      const currentUserStaff: DataTakerStaffDataModel = {
        StaffID: this.user?.ID ?? 0,
        FullName: this.user?.Fullnane ?? '',
        Email: null
      };
      this.staffMembers.set([currentUserStaff]);
      this.updateStaffOptions();
      
      // Preselect current user
      this.preselectCurrentUser();
    } else if (selectedSessionValue.Staffs && selectedSessionValue.Staffs.length > 0) {
        this.staffMembers.set(selectedSessionValue.Staffs);
        this.updateStaffOptions();
        
        // preselect staff from session
        this.preselectFirstStaffFromSession(selectedSessionValue.Staffs);
    }
  }
  
  loadStaffFromSession(session: PatientSessionModel | SessionSimpleDataModel) {
    // When session is selected, reload staff members (includes session staff)
    this.loadStaffMembersForSelection();
  }

  preselectCurrentUser() {
    if (this.user && this.user.ID) {
      const currentUserOption = this.staffOptions().find(s => s.value?.StaffID === this.user!.ID);
      if (currentUserOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: currentUserOption.label,
          value: currentUserOption.value
        };
        this.addBehaviorForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }

  preselectFirstStaffFromSession(staff: DataTakerStaffDataModel[]) {
    if (staff && staff.length > 0) {
      const staffOption = this.staffOptions().find(s => s.value?.StaffID === staff[0].StaffID);
      if (staffOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: staffOption.label,
          value: staffOption.value
        };
        this.addBehaviorForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }
  
  updateStaffOptions() {
    this.staffOptions.set(
      this.staffMembers().map(staff => ({
        label: staff.FullName ?? '',
        value: staff
      }))
    );
  }
  
  changedBehavior(event: any) {
    if (event.value) {
      this.selectedBehavior.set(event.value.value);
      this.loadStaffMembersForSelection();
    }
  }

  formattedDates(startDate: string | null | undefined, endDate: string | null | undefined) {
    if (!startDate || !endDate) {
      return 'Not started';
    }

    //if on the same day, return the date and 2 times
    if (new Date(startDate).toDateString() === new Date(endDate).toDateString()) {
      let result = new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
      //add end time to the result
      result += ' - ' + new Date(endDate).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      return result;
    }

    if (!endDate) {
      return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' - not finished';
    }

    //date in fomrat MM/DD/YYYY hh:mm AM/PM
    return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' - ' + new Date(endDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  }

  loadAllData() {
    this.isLoading.set(true);

    const behaviorRequest = this.staffApiService.GetPatientBehaviors({ patientID: this.selectedPatientID ?? 0 });
    const antecedentRequest = this.staffApiService.GetPatientBehaviorAntecedents({ patientID: this.selectedPatientID ?? 0 });
    const consequenceRequest = this.staffApiService.GetPatientBehaviorConsequences({ patientID: this.selectedPatientID ?? 0 });
    const severityRequest = this.staffApiService.GetPatientBehaviorSeverities({});
    const responseRequest = this.staffApiService.GetPatientBehaviorResponses({});
    const locationRequest = this.staffApiService.GetPatientBehaviorLocations({});

    forkJoin({
      behaviors: behaviorRequest,
      antecedents: antecedentRequest, 
      consequences: consequenceRequest,
      severities: severityRequest,
      responses: responseRequest,
      locations: locationRequest
    }).subscribe({
      next: (results) => {
        this.behaviors.set(results.behaviors.filter((b) => !b.IsDeleted && !b.IsArchived).map((b) => ({ 
          value: b, 
          label: this.displayNameForBehavior(b) 
        })));

        this.antecedents.set(results.antecedents);
        this.consequences.set(results.consequences);
        this.severities.set(results.severities);
        this.responses.set(results.responses);
        this.locations.set(results.locations);

        const now = new Date();
        const localISOString = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
          .toISOString()
          .slice(0, 16);
        this.addBehaviorForm.controls.recordedDate.setValue(localISOString);
        this.addBehaviorForm.controls.recordedDate.updateValueAndValidity();

        this.isLoading.set(false);
      },
      error: (error) => {
        this.isLoading.set(false);
        this.alertService.error('Error loading behavior data');
      }
    });
  }

  displayNameForBehavior(behavior: PatientBehaviorModel) {
    return behavior.PatientBehaviorName + ' (' + Utils.patientBehaviorTypeLabel(behavior?.BehaviorType!) + ')';
  }

  intValueForBehavior() {
    if (this.behaviorIsFrequency()) {
      return 1;
    } else if (this.behaviorIsDuration()) {
      return this.minutesToSeconds(this.addBehaviorForm.controls.duration.value!);
    } else if (this.behaviorIsInterval()) {
      return this.addBehaviorForm.controls.happenedDuringInterval.value ? 1 : 0;
    } else {
      return this.addBehaviorForm.controls.textValue.value;
    }
  }

  textValueForBehavior() {
    if (this.behaviorIsFrequency()) {
      return '1';
    } else if (this.behaviorIsDuration()) {
      const minutes = this.addBehaviorForm.controls.duration.value!;
      const seconds = this.minutesToSeconds(minutes);
      // format from seconds to hh:mm string
      const minutes1 = Math.floor((seconds % 3600) / 60);
      const seconds1 = seconds % 60;
      return `${minutes1 < 10 ? '0' + minutes1 : minutes1}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
    } else if (this.behaviorIsInterval()) {
      return this.addBehaviorForm.controls.happenedDuringInterval.value ? '1' : '0';
    } else {
      return this.addBehaviorForm.controls.textValue.value;
    }
  }

  behaviorIsFrequency() {
    return this.selectedBehavior()?.BehaviorType === PatientBehaviorType.Frequency;
  }

  behaviorIsDuration() {
    return this.selectedBehavior()?.BehaviorType === PatientBehaviorType.Duration;
  }

  behaviorIsInterval() {
    return this.selectedBehavior()?.BehaviorType === PatientBehaviorType.Interval;
  }

  onSubmit() {
    console.log(this.addBehaviorForm.value);

    // validate form
    if (this.addBehaviorForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }

    const recordedDateToSend = this.addBehaviorForm.controls.recordedDate.value ? 
    this.addBehaviorForm.controls.recordedDate.value : 
    new Date().toServerString();

    
    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }

    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }

    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.ID;

    // Get selected staff or use current user
    const selectedStaffValue = this.addBehaviorForm.controls.selectedStaff.value;
    const staffID = selectedStaffValue?.value?.StaffID ?? this.user?.ID ?? 0;
    const staffFullName = selectedStaffValue?.value?.FullName ?? this.user?.Fullnane ?? '';
    
    // build behavior data to be saved
    const behaviorData: PatientBehaviorDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientBehaviorID: this.selectedBehavior()?.ID ?? 0,
      RecordedDate: recordedDateToSend,
      IntValue: Number(this.intValueForBehavior()) ?? null,
      TextValue: this.textValueForBehavior() ?? null,
      UserID: staffID, // Use selected staff from dropdown, or current user
      UserFullName: staffFullName,
      Notes: this.addBehaviorForm.controls.notes.value ?? null,
      PatientBehaviorSeverityID: this.selectedSeverity()?.ID ?? null,
      PatientBehaviorResponseID: this.selectedResponse()?.ID ?? null,
      PatientBehaviorLocationID: this.selectedLocation()?.ID ?? null,
      PatientBehaviorAntecedentIDs: this.selectedAntecedents()?.map((a) => a?.ID).filter((id): id is number => id != null) ?? [],
      PatientBehaviorConsequenceIDs: this.selectedConsequences()?.map((c) => c?.ID).filter((id): id is number => id != null) ?? [],
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here

    console.log(behaviorData);

    // append new target data to patient session
    this.saveBehaviorData(behaviorData);
  }

  saveBehaviorData(behaviorData: PatientBehaviorDataModel) {
    this.isLoading.set(true);
    console.log('Saving session data:', this.patientSession);
    if (this.creatingSession()) {
      this.staffApiService.SavePatientSession({
        body: {
          PatientID: this.selectedPatientID,
          StartedDate: this.newSessionStartDate!,
          FinishedDate: this.newSessionEndDate!,
        },
      }).subscribe(
        {
          next: (response) => {
            console.log('Created session:', response);
            behaviorData.PatientSessionID = response.ID;
            this.doSaveBehaviorData(behaviorData);
          },
          error: (error) => {
            console.error('Error creating session:', error);
            this.alertService.error('Error creating session - ' + error.Message);
            this.isLoading.set(false);
          }
        }
      );
    } else {
      this.doSaveBehaviorData(behaviorData);
    }
  }

  doSaveBehaviorData(behaviorData: PatientBehaviorDataModel) {
    this.isLoading.set(true);
    this.staffApiService
      .SavePatientBehaviorData({
        addedManually: true,
        body: behaviorData,
      })
      .subscribe(
        (response) => {
          console.log('Saved behavior data:', response);
          this.alertService.success('Behavior data saved successfully');
          this.isLoading.set(false);
          this.close.emit(true);
        },
        (error) => {
          console.error('Error saving behavior data:', error);
          this.alertService.error(
            'Error saving behavior data - ' + error.Message
          );
          this.isLoading.set(false);
        }
      );
  }

  onCancel() {
    this.close.emit(true);
  }

  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }
  
  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  selectedSessionLabel(session: SessionSimpleDataModel) {
    if (session) {
      return 'Session: ' + this.formattedDates(session.StartedDate, session.FinishedDate) + (session.ServiceID ? (' (Service: #' + session.ServiceID + ' - ' + (session.Staffs?.map(st => st.FullName ?? '').join(', ') ?? []) + ')') : '');
    } else {
      return '';
    }
  }

  shouldShowStaffSelector() {
    return !this.hideSessionSelector && this.selectedSession !== null && this.selectedSession !== undefined && !this.creatingSession();
  }
}
